import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const api = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('auth_user');
      if (window.location.pathname.startsWith('/admin')) {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth
export const login = (email, password) => api.post('/api/auth/login', { email, password });
export const register = (data) => api.post('/api/auth/register', data);
export const getMe = () => api.get('/api/auth/me');
export const requestPasswordReset = (email) => {
  const formData = new FormData();
  formData.append('email', email);
  return api.post('/api/auth/reset-request', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
};
export const resetPassword = (resetCode, newPassword) => {
  const formData = new FormData();
  formData.append('reset_code', resetCode);
  formData.append('new_password', newPassword);
  return api.post('/api/auth/reset-password', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
};

// Settings
export const getSettings = () => api.get('/api/settings');
export const updateSettings = (settings) => api.put('/api/settings', { settings });

// CMS
export const getAllCMS = () => api.get('/api/cms');
export const getCMS = (key) => api.get(`/api/cms/${key}`);
export const updateCMS = (key, data) => api.put(`/api/cms/${key}`, data);

// Animals
export const getAnimals = (params) => api.get('/api/animals', { params });
export const getAnimal = (id) => api.get(`/api/animals/${id}`);
export const createAnimal = (data) => api.post('/api/animals', data);
export const updateAnimal = (id, data) => api.put(`/api/animals/${id}`, data);
export const deleteAnimal = (id) => api.delete(`/api/animals/${id}`);
export const uploadAnimalPhoto = (id, file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post(`/api/animals/${id}/photos`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};
export const deleteAnimalPhoto = (id, photoUrl) =>
  api.delete(`/api/animals/${id}/photos`, { params: { photo_url: photoUrl } });
export const setAnimalMainPhoto = (id, photoUrl) =>
  api.put(`/api/animals/${id}/photos/main`, null, { params: { photo_url: photoUrl } });

// Litter photos
export const uploadLitterPhoto = (id, file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post(`/api/litters/${id}/photos`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
};
export const setLitterMainPhoto = (id, photoUrl) =>
  api.put(`/api/litters/${id}/photos/main`, null, { params: { photo_url: photoUrl } });
export const deleteLitterPhoto = (id, photoUrl) =>
  api.delete(`/api/litters/${id}/photos`, { params: { photo_url: photoUrl } });

// Inquiries
export const createInquiry = (data) => api.post('/api/inquiries', data);
export const getInquiries = () => api.get('/api/inquiries');
export const updateInquiry = (id, data) => {
  const formData = new FormData();
  formData.append('status', data.status || 'active');
  formData.append('admin_notes', data.admin_notes || '');
  return api.put(`/api/inquiries/${id}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};
export const deleteInquiry = (id) => api.delete(`/api/inquiries/${id}`);

// Users
export const deleteUser = (id) => api.delete(`/api/users/${id}`);
export const adminCreateUser = (data) => {
  const formData = new FormData();
  Object.entries(data).forEach(([k, v]) => formData.append(k, v));
  return api.post('/api/users/create', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
};
export const adminResetPassword = (userId) => api.post(`/api/users/${userId}/reset-password`);

// Reviews
export const getReviews = (params) => api.get('/api/reviews', { params });
export const createReview = (data) => api.post('/api/reviews', data);
export const createReviewAdmin = (data) => api.post('/api/reviews/admin', data);
export const updateReview = (id, data) => api.put(`/api/reviews/${id}`, null, { params: data });
export const deleteReview = (id) => api.delete(`/api/reviews/${id}`);
export const uploadReviewPhoto = (id, file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post(`/api/reviews/${id}/photos`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

// Affiliates
export const getAffiliates = (params) => api.get('/api/affiliates', { params });
export const createAffiliate = (data) => api.post('/api/affiliates', data);
export const updateAffiliate = (id, data) => api.put(`/api/affiliates/${id}`, data);
export const deleteAffiliate = (id) => api.delete(`/api/affiliates/${id}`);

// Media
export const uploadMedia = (file, category = 'gallery', caption = '', isPublic = true) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('category', category);
  formData.append('caption', caption);
  formData.append('is_public', isPublic);
  return api.post('/api/media/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};
// Media with progress
export const uploadMediaWithProgress = (file, category, caption, isPublic, onProgress) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('category', category || 'gallery');
  formData.append('caption', caption || '');
  formData.append('is_public', isPublic !== false);
  return api.post('/api/media/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress: (e) => { if (onProgress && e.total) onProgress(Math.round((e.loaded * 100) / e.total)); },
  });
};
export const getMedia = (params) => api.get('/api/media', { params });
export const deleteMedia = (id) => api.delete(`/api/media/${id}`);
export const toggleMediaVisibility = (id) => api.put(`/api/media/${id}/toggle`);

// Banner Upload
export const uploadBanner = (file, bannerType = 'landing') => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('banner_type', bannerType);
  return api.post('/api/settings/banner', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};
export const uploadSectionBgImage = (file, section) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('section', section);
  return api.post('/api/settings/section-bg-image', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
};

// Announcements
export const getAnnouncements = (params) => api.get('/api/announcements', { params });
export const createAnnouncement = (data) => api.post('/api/announcements', data);
export const updateAnnouncement = (id, data) => api.put(`/api/announcements/${id}`, data);
export const deleteAnnouncement = (id) => api.delete(`/api/announcements/${id}`);
export const uploadAnnouncementMedia = (id, file, onProgress) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post(`/api/announcements/${id}/media`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress: (e) => { if (onProgress && e.total) onProgress(Math.round((e.loaded * 100) / e.total)); },
  });
};

// Public
export const getLandingData = () => api.get('/api/public/landing');
export const getPublicRatgeber = () => api.get('/api/public/ratgeber');
export const getPublicRatgeberChapter = (key) => api.get(`/api/public/ratgeber/${key}`);

// CMS Image Upload
export const uploadCMSImage = (key, file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post(`/api/cms/${key}/image`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
};

// Contract Templates
export const getContractTemplates = () => api.get('/api/contracts/templates');
export const updateContractTemplate = (type, data) => api.put(`/api/contracts/templates/${type}`, data);

// Litters
export const getLitters = (params) => api.get('/api/litters', { params });
export const createLitter = (data) => api.post('/api/litters', data);
export const updateLitter = (id, data) => api.put(`/api/litters/${id}`, data);
export const deleteLitter = (id) => api.delete(`/api/litters/${id}`);

// Kittens
export const getKittens = (params) => api.get('/api/kittens', { params });
export const createKitten = (data) => api.post('/api/kittens', data);
export const updateKitten = (id, data) => api.put(`/api/kittens/${id}`, data);
export const deleteKitten = (id) => api.delete(`/api/kittens/${id}`);
export const uploadKittenPhoto = (id, file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post(`/api/kittens/${id}/photos`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
};

// Buyers
export const getBuyers = () => api.get('/api/buyers');
export const updateBuyerProfile = (userId, data) => api.post(`/api/buyers/${userId}`, data);
export const getMyBuyerProfile = () => api.get('/api/buyers/me');

// Buyer Exclusive Media
export const uploadBuyerMedia = (buyerUserId, file, caption, onProgress) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('caption', caption || '');
  return api.post(`/api/buyers/${buyerUserId}/media`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress: (e) => { if (onProgress && e.total) onProgress(Math.round((e.loaded * 100) / e.total)); },
  });
};
export const getBuyerMedia = (buyerUserId) => api.get(`/api/buyers/${buyerUserId}/media`);
export const deleteBuyerMedia = (mediaId) => api.delete(`/api/buyers/media/${mediaId}`);

// Knowledge
export const getKnowledge = () => api.get('/api/knowledge');
export const getKnowledgeChapter = (key) => api.get(`/api/knowledge/${key}`);
export const searchKnowledge = (query) => api.get(`/api/knowledge/search/${encodeURIComponent(query)}`);
export const createKnowledgeChapter = (data) => api.post('/api/knowledge', data);
export const deleteKnowledgeChapter = (key) => api.delete(`/api/knowledge/${key}`);
export const deleteKnowledgeImage = (key, imageUrl) =>
  api.delete(`/api/knowledge/${key}/images`, { params: { image_url: imageUrl } });
export const uploadKnowledgeImage = (key, file, position) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('position', position || 'top');
  return api.post(`/api/knowledge/${key}/images`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
};

// Contracts
export const getContracts = (params) => api.get('/api/contracts', { params });
export const createContract = (data) => api.post('/api/contracts', data);
export const updateContractFull = (id, data) => api.put(`/api/contracts/${id}`, data);
export const deleteContract = (id) => api.delete(`/api/contracts/${id}`);
export const approveContract = (id) => api.put(`/api/contracts/${id}/approve`);
export const updateContractText = (id, data) => api.put(`/api/contracts/${id}/text`, data);
export const adminSignContract = (id, data) => {
  const formData = new FormData();
  formData.append('signed_name', data.signed_name);
  if (data.signature_data) formData.append('signature_data', data.signature_data);
  return api.post(`/api/contracts/${id}/admin-sign`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
};
export const signContract = (id, data) => {
  const formData = new FormData();
  formData.append('signed_name', data.signed_name);
  formData.append('signature_data', data.signature_data);
  return api.post(`/api/contracts/${id}/sign`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};
export const getContractPDF = (id) => api.get(`/api/contracts/${id}/pdf`, { responseType: 'blob' });

// Admin Preview
export const previewBuyerDashboard = (userId) => api.get(`/api/admin/preview-dashboard/${userId}`);

// Dashboard
export const getDashboardData = () => api.get('/api/dashboard/data');

export default api;
